/**
 * Confetti particle system for celebratory animations
 */

const DEG_TO_RAD = Math.PI / 180
const TWO_PI = Math.PI * 2

const COLORS = [
  '#FFD700',
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#FFEAA7',
  '#DDA0DD',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE'
]

export class ConfettiParticle {
  x: number
  y: number
  size: number
  rotation: number
  rotationSpeed: number
  opacity: number
  containerWidth: number
  containerHeight: number
  floatOffset: number
  floatSpeed: number
  floatAmplitude: number
  horizontalDrift: number
  baseY: number
  color: string

  constructor(x: number, y: number, containerWidth: number, containerHeight: number) {
    this.x = x
    this.y = y
    this.baseY = y

    this.size = Math.random() * 10 + 3
    this.rotation = Math.random() * 360
    this.rotationSpeed = (Math.random() - 0.5) * 2
    this.opacity = 1

    this.containerWidth = containerWidth
    this.containerHeight = containerHeight

    // Floating properties
    this.floatOffset = Math.random() * TWO_PI
    this.floatSpeed = 0.01 + Math.random() * 0.02
    this.floatAmplitude = 3 + Math.random() * 4
    this.horizontalDrift = (Math.random() - 0.5) * 0.5

    // Shared color palette (no per-instance allocation)
    this.color = COLORS[(Math.random() * COLORS.length) | 0]
  }

  update(): boolean {
    this.floatOffset += this.floatSpeed

    const sin = Math.sin(this.floatOffset)

    this.y = this.baseY + sin * this.floatAmplitude
    this.x += this.horizontalDrift * sin
    this.rotation += this.rotationSpeed

    const buffer = this.size * 2
    if (this.x < -buffer) this.x = this.containerWidth + buffer
    else if (this.x > this.containerWidth + buffer) this.x = -buffer

    return true
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    ctx.globalAlpha = this.opacity
    ctx.translate(this.x, this.y)
    ctx.rotate(this.rotation * DEG_TO_RAD)
    ctx.fillStyle = this.color
    ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size)
    ctx.restore()
  }
}

/**
 * Confetti system configuration options
 */
export interface ConfettiConfig {
  particleCount?: number
}

/**
 * Create an array of confetti particles
 */
export function createConfettiParticles(
  containerWidth: number,
  containerHeight: number,
  config: ConfettiConfig = {}
): ConfettiParticle[] {
  const { particleCount = 30 } = config

  const particles: ConfettiParticle[] = []

  for (let i = 0; i < particleCount; i++) {
    particles.push(
      new ConfettiParticle(
        Math.random() * containerWidth,
        Math.random() * containerHeight,
        containerWidth,
        containerHeight
      )
    )
  }

  return particles
}
